import { useState, useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { ListMeta } from '../types/api';

/**
 * Paginated, filterable list over any /api/v1 collection. Every list page in the app
 * is this hook plus a column definition — which is how thirty screens stay consistent.
 */
export function useList<T>(path: string, initialFilters: Record<string, unknown> = {}, options: { perPage?: number; enabled?: boolean } = {}) {
  const [page, setPage] = useState(1);
  const [filters, setFiltersState] = useState<Record<string, unknown>>(initialFilters);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<string | undefined>();

  const query = useMemo(() => ({ ...filters, page, perPage: options.perPage ?? 25, search: search || undefined, sort }), [filters, page, options.perPage, search, sort]);

  const result = useQuery({
    queryKey: [path, query],
    queryFn: async () => api.get<T[]>(path, query),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });

  const setFilters = (next: Record<string, unknown>) => { setPage(1); setFiltersState(next); };
  const setFilter = (key: string, value: unknown) => setFilters({ ...filters, [key]: value === '' ? undefined : value });

  return {
    rows: (result.data?.data ?? []) as T[],
    meta: result.data?.meta as ListMeta | undefined,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    error: result.error as Error | null,
    refetch: result.refetch,
    page, setPage, filters, setFilters, setFilter, search, setSearch: (s: string) => { setPage(1); setSearch(s); }, sort, setSort,
  };
}
